import React, { useState, useEffect, useRef } from "react";
import ConfirmModal from "../Modals/ConfirmModal";
import { useNavigate } from "react-router-dom";
import { useFinanzas } from "../../context/FinanzasContext";
import { FaEllipsisH } from "react-icons/fa";

const PagosTable = ({ showNotification, searchQuery, filterYear, filterMonth }) => {
  const navigate = useNavigate();
  const { pagos, removePago } = useFinanzas();
  const [selectedPagoId, setSelectedPagoId] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [actionMenu, setActionMenu] = useState(null);
  const [actionMenuPosition, setActionMenuPosition] = useState({ top: 0, left: 0 });
  const actionMenuRef = useRef(null);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const pagosPorPagina = 4; 

  
  // Filtrar por nombre, año y mes
  const filteredPagos = pagos.filter((pago) => {
    const fullName = `${pago.first_name} ${pago.second_name} ${pago.first_surname} ${pago.second_surname}`.toLowerCase();
    const matchesName = searchQuery === "" || fullName.includes(searchQuery.toLowerCase());
    const matchesYear = !filterYear || new Date(pago.fecha).getFullYear().toString() === filterYear;
    const matchesMonth = !filterMonth || (new Date(pago.fecha).getMonth() + 1).toString() === filterMonth;
    
    return matchesName && matchesYear && matchesMonth;
  });

  const indexOfLastPago = currentPage * pagosPorPagina;
  const indexOfFirstPago = indexOfLastPago - pagosPorPagina;
  const pagosActuales = filteredPagos.slice(indexOfFirstPago, indexOfLastPago);
  const totalPaginas = Math.ceil(filteredPagos.length / pagosPorPagina);
  
  const openDeleteModal = (id) => {
    setSelectedPagoId(id);
    setIsModalOpen(true);
  };

  const closeDeleteModal = () => {
    setSelectedPagoId(null);
    setIsModalOpen(false);
  };

  const handleDelete = async () => {
    if (selectedPagoId) {
      setIsLoading(true);
      try {
        await removePago(selectedPagoId);
        showNotification({
          type: "success",
          message: "Pago eliminado con éxito.",
        });
      } catch (error) {
        showNotification({
          type: "error",
          message: "Error al eliminar el pago. Inténtalo de nuevo.",
        });
      } finally {
        setIsLoading(false);
        closeDeleteModal();
      }
    }
  };

  const toggleActionMenu = (userId, event) => {
    if (actionMenu === userId) {
      setActionMenu(null);
    } else {
      const buttonRect = event.currentTarget.getBoundingClientRect();
      const menuWidth = 150; // Ancho aproximado del menú
      const leftPosition = Math.min(
        buttonRect.left,
        window.innerWidth - menuWidth - 70 // Evita que se salga del viewport
      );

      setActionMenu(userId);
      setActionMenuPosition({
        top: buttonRect.bottom + 10, // Espacio debajo del botón
        left: leftPosition,
      });
    }
  };

  // Cerrar el menú al hacer clic fuera o al redimensionar
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (actionMenuRef.current && !actionMenuRef.current.contains(e.target)) {
        setActionMenu(null);
      }
    };

    const handleResize = () => {
      setActionMenu(null);
    };

    document.addEventListener("mousedown", handleClickOutside);
    window.addEventListener("resize", handleResize);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  return (
    <div className="overflow-x-auto">
      <table className="w-full table-auto">
        <thead>
          <tr className="text-left text-main-blue">
            <th className="py-2 px-4">Nombre</th>
            <th className="py-2 px-4">Razón Pago</th>
            <th className="py-2 px-4">Fecha</th>
            <th className="py-2 px-4">Grado</th>
            <th className="py-2 px-4">Monto</th>
            <th className="py-2 px-4">Acciones</th>
          </tr>
        </thead>
        <tbody>
          {pagosActuales.map((pago) => (
            <tr key={pago.pago_id} className="hover:bg-gray-200">
              <td className="py-2 px-4">{`${pago.first_name} ${pago.second_name} ${pago.first_surname} ${pago.second_surname}`}</td>
              <td className="py-2 px-4">{pago.razon_pago}</td>
              <td className="py-2 px-4 text-green-600 font-bold">{pago.fecha}</td>
              <td className="py-2 px-4">{pago.grado}</td>
              <td className="py-2 px-4 text-blue-900 font-bold">${pago.monto.toLocaleString()}</td>
              <td
                className="relative px-4 py-3 overflow-visible"
              >
                <div className="flex items-center justify-center">
                  <button
                    onClick={(e) => toggleActionMenu(pago.pago_id, e)}
                    className="text-gray hover:text-black"
                  >
                    <FaEllipsisH />
                  </button>
                  {actionMenu === pago.pago_id && (
                    <div
                      ref={actionMenuRef}
                      className="fixed w-32 bg-white rounded-2xl shadow-lg z-50"
                      style={{
                        top: `${actionMenuPosition.top}px`,
                        left: `${actionMenuPosition.left}px`,
                      }}
                    >
                      <ul>
                        <li
                          className="px-4 py-2 text-main-bg-sidebar hover:bg-main-bg-sidebar hover:text-white cursor-pointer"
                          onClick={() =>
                            navigate(`/finanzas/editar/${pago.pago_id}`)
                          }
                        >
                          Actualizar
                        </li>
                        <li
                          className="px-4 py-2 text-red-600 hover:text-white hover:bg-red-700 cursor-pointer"
                          onClick={() => openDeleteModal(pago.pago_id)}
                        >
                          Eliminar
                        </li>
                      </ul>
                    </div>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Paginación */}
      <div className="flex justify-end items-center mt-6 pr-4">
        <button
          onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
          disabled={currentPage === 1}
          className="w-10 h-10 flex justify-center items-center rounded-full text-gray hover:bg-gray-200"
        >
          ◀
        </button>
        {[...Array(totalPaginas).keys()].map((index) => (
          <button
            key={index}
            onClick={() => setCurrentPage(index + 1)}
            className={`w-10 h-10 mx-2 flex justify-center items-center border-2 rounded-full ${
              currentPage === index + 1 ? "bg-main-blue text-white" : "hover:bg-gray-200"
            }`}
          >
            {index + 1}
          </button>
        ))}
        <button
          onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPaginas))}
          disabled={currentPage === totalPaginas}
          className="w-10 h-10 flex justify-center items-center rounded-full text-gray hover:bg-gray-200"
        >
          ▶
        </button>
      </div>

      <ConfirmModal
        isOpen={isModalOpen}
        title="Confirmar Eliminación"
        message="¿Estás seguro de que deseas eliminar este pago? Esta acción no se puede deshacer."
        onConfirm={handleDelete}
        onCancel={closeDeleteModal}
        isLoading={isLoading}
      />
    </div>
  );
};

export default PagosTable;
